import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import Swal from 'sweetalert2';

const ROLES = [
  { key: 'admin', label: 'Admin' },
  { key: 'manager', label: 'Manager' },
  { key: 'cashier', label: 'Cashier' },
];

const hashPin = async (pin, salt) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + salt);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const KioskPage = () => {
  const { kioskId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [kiosk, setKiosk] = useState(null);
  const [location, setLocation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [session, setSession] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  // Employee back modal
  const [employees, setEmployees] = useState([]);
  const [showBackModal, setShowBackModal] = useState(false);
  const [backEmployeeId, setBackEmployeeId] = useState('');
  const [backEmployeePin, setBackEmployeePin] = useState('');
  // Product Options Modal
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsProduct, setOptionsProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({}); // { optionId: Set([name1, name2]) }
  const [optionsExtraFees, setOptionsExtraFees] = useState({}); // { name: fee }
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [modalError, setModalError] = useState('');
  // Quantity picker modal
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [qtyProduct, setQtyProduct] = useState(null);
  const [qtyValue, setQtyValue] = useState(1);
  const [optionsQty, setOptionsQty] = useState(1);
  const categoryTabsRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Inactivity timer: clear cart after configured idle timeout minutes of no interaction
  useEffect(() => {
    const ACTIVITY_EVENTS = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    const IDLE_MS = (kiosk?.idleTimeoutMinutes || 5) * 60 * 1000;
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, updateActivity, { passive: true }));

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_MS) {
        setCart([]);
        setShowCart(false);
        setShowOptionsModal(false);
        setShowQtyModal(false);
        setOptionsProduct(null);
        setQtyProduct(null);
        setSelectedOptions({});
        setOptionsExtraFees({});
        setSpecialInstructions('');
        setModalError('');
        lastActivityRef.current = Date.now();
      }
    }, 10000);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, updateActivity));
      clearInterval(interval);
    };
  }, [kiosk?.idleTimeoutMinutes]);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Restore cart when returning from checkout
  useEffect(() => {
    if (routerLocation.state?.cart?.length > 0) {
      setCart(routerLocation.state.cart);
      // Clear the state so it doesn't re-apply on refresh
      window.history.replaceState({}, document.title);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadKioskData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  const loadKioskData = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();

      // Load kiosk
      const kioskDoc = await getDoc(doc(db, 'users', uid, 'vkiosks', kioskId));
      if (!kioskDoc.exists()) {
        Swal.fire('Error', 'Kiosk not found', 'error');
        navigate('/admin/vkiosks');
        return;
      }
      const kioskData = { id: kioskDoc.id, ...kioskDoc.data() };
      setKiosk(kioskData);

      // Load location
      let locData = null;
      if (kioskData.locationId) {
        const locDoc = await getDoc(doc(db, 'users', uid, 'locations', kioskData.locationId));
        if (locDoc.exists()) {
          locData = { id: locDoc.id, ...locDoc.data() };
          setLocation(locData);
        }
      }

      // Load categories ordered by order
      const catSnap = await getDocs(query(collection(db, 'users', uid, 'categories'), orderBy('order')));
      const cats = catSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCategories(cats);

      // Load products from each category's products subcollection
      const allProducts = [];
      for (const cat of cats) {
        const prodSnap = await getDocs(
          query(collection(db, 'users', uid, 'categories', cat.id, 'products'), orderBy('order'))
        );
        prodSnap.docs.forEach((d) => {
          allProducts.push({
            id: d.id,
            categoryId: cat.id,
            category: cat.name || cat.id,
            ...d.data(),
          });
        });
      }
      // Product-level location scoping (empty/missing = all locations).
      const kioskLocationId = kioskData.locationId || null;
      setProducts(allProducts.filter((p) => {
        const locs = p.availableLocations;
        if (!Array.isArray(locs) || locs.length === 0) return true;
        return kioskLocationId ? locs.includes(kioskLocationId) : true;
      }));

      // Load product options
      const optSnap = await getDocs(collection(db, 'users', uid, 'productOptions'));
      setProductOptions(optSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      // Load employees with admin/manager/cashier roles
      const empSnap = await getDocs(
        query(collection(db, 'users', uid, 'employees'), where('status', '==', 'active'))
      );
      const allowedRoles = ['admin', 'manager', 'cashier'];
      const emps = empSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((e) => allowedRoles.includes((e.role || '').toLowerCase()));
      setEmployees(emps);

      // Session management — prefer sessionStorage for exact session reuse on reload,
      // then fall back to querying Firestore for any open session.
      let activeSession = null;
      let activeSessionId = null;

      // 1) Try sessionStorage first (survives reload / back-navigation)
      try {
        const stored = sessionStorage.getItem(`activeKioskSession_${kioskId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.sessionId) {
            const storedSessionDoc = await getDoc(doc(db, 'users', uid, 'kioskSessions', parsed.sessionId));
            if (storedSessionDoc.exists() && storedSessionDoc.data().status === 'open') {
              activeSessionId = parsed.sessionId;
              activeSession = { id: activeSessionId, ...storedSessionDoc.data() };
            }
          }
        }
      } catch (storageErr) {
        console.warn('Could not read stored kiosk session:', storageErr.message);
      }

      // 2) Fall back to Firestore query for any open session
      if (!activeSessionId) {
        const sessionsSnap = await getDocs(
          query(
            collection(db, 'users', uid, 'kioskSessions'),
            where('kioskId', '==', kioskId),
            where('status', '==', 'open')
          )
        );
        if (!sessionsSnap.empty) {
          activeSession = { id: sessionsSnap.docs[0].id, ...sessionsSnap.docs[0].data() };
          activeSessionId = activeSession.id;
        } else {
          // 3) Create new session
          const newSessionRef = doc(collection(db, 'users', uid, 'kioskSessions'));
          activeSessionId = newSessionRef.id;
          const sessionPayload = {
            kioskId,
            locationId: kioskData.locationId || null,
            status: 'open',
            startTime: serverTimestamp(),
            totalSales: 0,
            totalTransactions: 0,
            cash: 0,
            cardSales: 0,
            stripeSales: 0,
          };
          await setDoc(newSessionRef, sessionPayload);
          activeSession = { id: activeSessionId, ...sessionPayload };
        }
      }

      setSession(activeSession);
      setSessionId(activeSessionId);

      // Persist in both sessionStorage (exact tab) and localStorage (tab lock)
      try {
        sessionStorage.setItem(`activeKioskSession_${kioskId}`, JSON.stringify({ sessionId: activeSessionId }));
      } catch (e) {
        console.warn('Error storing kiosk session in sessionStorage:', e);
      }
      try {
        localStorage.setItem('kioskSessionLock', JSON.stringify({
          userId: uid,
          kioskId,
          sessionId: activeSessionId,
          timestamp: new Date().toISOString(),
        }));
      } catch (e) {
        console.error('Error setting kiosk session lock:', e);
      }
    } catch (e) {
      console.error('Error loading kiosk data:', e);
      Swal.fire('Error', 'Failed to load kiosk data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter((p) => {
    const matchesCategory =
      selectedCategory === 'all' ? true :
      selectedCategory === '__deals__' ? p.onSale === true :
      p.category === selectedCategory;
    const matchesSearch = searchQuery.trim() === '' ||
      (p.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Helper: resolve workzoneId from a product's category for the current kiosk location
  const getWorkzoneId = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.workzonesByLocation?.[location?.id] || null;
  };

  const addToCart = (product) => {
    if (product.optionIds?.length > 0 || product.acceptSpecialRequirement) {
      setOptionsProduct(product);
      setSelectedOptions({});
      setOptionsExtraFees({});
      setSpecialInstructions('');
      setModalError('');
      setOptionsQty(1);
      setShowOptionsModal(true);
      return;
    }
    // Simple product: show quantity picker first
    setQtyProduct(product);
    setQtyValue(1);
    setShowQtyModal(true);
  };

  const finalizeAddToCart = (product, options, extraFeeTotal, instructions, quantity = 1) => {
    const basePrice = product.onSale && product.salePrice !== undefined ? product.salePrice : (product.price || 0);
    const finalPrice = basePrice + extraFeeTotal;
    const qty = Math.max(1, Math.floor(quantity || 1));
    setCart((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        productDescription: product.description || '',
        productImage: product.imageUrl || null,
        categoryId: product.categoryId || null,
        categoryName: product.category || '',
        workzoneId: getWorkzoneId(product.categoryId) || null,
        price: finalPrice,
        qty: qty,
        total: finalPrice * qty,
        options: options || [],
        instructions: instructions || null,
        date: new Date(),
      },
    ]);
    setShowCart(true);
  };

  const updateQuantity = (productId, delta, optionsKey) => {
    setCart((prev) => {
      return prev
        .map((item) => {
          const itemOptionsKey = JSON.stringify(item.options || []);
          const match =
            item.productId === productId &&
            (optionsKey === undefined || itemOptionsKey === optionsKey);
          if (match) {
            const newQty = Math.max(0, (item.qty || 0) + delta);
            return { ...item, qty: newQty, total: item.price * newQty };
          }
          return item;
        })
        .filter((item) => item.qty > 0);
    });
  };

  const removeFromCart = (productId, optionsKey) => {
    setCart((prev) =>
      prev.filter((item) => {
        const itemOptionsKey = JSON.stringify(item.options || []);
        return !(
          item.productId === productId &&
          (optionsKey === undefined || itemOptionsKey === optionsKey)
        );
      })
    );
  };

  const cartSubtotal = cart.reduce(
    (sum, item) => sum + (item.total || (item.price || 0) * (item.qty || 0)),
    0
  );
  const cartCount = cart.reduce((sum, item) => sum + (item.qty || 0), 0);
  const taxRate = location?.taxRate || 0;
  const cartTax = cartSubtotal * (taxRate / 100);
  const cartTotal = cartSubtotal + cartTax;

  const handleBackButton = () => {
    setShowBackModal(true);
    setBackEmployeeId('');
    setBackEmployeePin('');
  };

  const verifyBackEmployee = async () => {
    if (!backEmployeeId) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }
    const emp = employees.find((e) => e.id === backEmployeeId);
    if (!emp) {
      Swal.fire('Error', 'Employee not found', 'error');
      return;
    }
    // Verify passkey if set
    if (emp.pinHash) {
      if (!backEmployeePin || backEmployeePin.length < 4) {
        Swal.fire('Error', 'Please enter the passkey (4-6 digits)', 'error');
        return;
      }
      const inputHash = await hashPin(backEmployeePin, emp.pinSalt);
      if (inputHash !== emp.pinHash) {
        Swal.fire('Error', 'Incorrect passkey', 'error');
        return;
      }
    }
    // Verified — clear session lock and navigate
    try {
      localStorage.removeItem('kioskSessionLock');
      sessionStorage.removeItem(`activeKioskSession_${kioskId}`);
    } catch (e) {}
    setShowBackModal(false);
    navigate('/admin/vkiosks');
  };

  const handleGoToCheckout = () => {
    navigate(`/admin/kiosk-checkout/${kioskId}`, {
      state: { cart, kiosk, location, sessionId },
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
        }}
      >
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 120, height: 120, objectFit: 'contain' }} />
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#f5f5f5',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          background: '#fff',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={handleBackButton}
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              border: '1px solid #e0e0e0',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 20,
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          {location?.logoUrl ? (
            <img
              src={location.logoUrl}
              alt="Logo"
              style={{ height: 44, objectFit: 'contain', borderRadius: 8 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : null}
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a' }}>
              {location?.companyName || location?.locationName || 'Kiosk'}
            </div>
            <div style={{ fontSize: 14, color: '#888' }}>
              {kiosk?.kioskName || kioskId}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, color: '#888' }}>Session Started</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
              {formatTime(session?.startTime)}
            </div>
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: '#1a1a1a',
              background: '#f0f0f0',
              padding: '8px 20px',
              borderRadius: 12,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <button
            onClick={() => setShowCart(true)}
            style={{
              position: 'relative',
              width: 56,
              height: 56,
              borderRadius: 16,
              border: 'none',
              background: '#ff6b35',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="fas fa-shopping-bag"></i>
            {cartCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  background: '#1a1a1a',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 800,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div style={{ position: 'relative', flexShrink: 0, background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
        {/* Left scroll arrow */}
        <button
          onClick={() => {
            if (categoryTabsRef.current) {
              categoryTabsRef.current.scrollBy({ left: -200, behavior: 'smooth' });
            }
          }}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 48,
            zIndex: 10,
            border: 'none',
            background: 'linear-gradient(to right, #fff, rgba(255,255,255,0))',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingLeft: 8,
            color: '#ff6b35',
            fontSize: 20,
          }}
        >
          <i className="fas fa-chevron-left"></i>
        </button>

        {/* Right scroll arrow */}
        <button
          onClick={() => {
            if (categoryTabsRef.current) {
              categoryTabsRef.current.scrollBy({ left: 200, behavior: 'smooth' });
            }
          }}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 48,
            zIndex: 10,
            border: 'none',
            background: 'linear-gradient(to left, #fff, rgba(255,255,255,0))',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: 8,
            color: '#ff6b35',
            fontSize: 20,
          }}
        >
          <i className="fas fa-chevron-right"></i>
        </button>

        <div
          ref={categoryTabsRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 48px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            flexWrap: 'nowrap',
          }}
        >
          <button
            onClick={() => setSelectedCategory('all')}
            style={{
              flexShrink: 0,
              padding: '12px 24px',
              borderRadius: 50,
              border: 'none',
              fontSize: 18,
              fontWeight: 700,
              cursor: 'pointer',
              background: selectedCategory === 'all' ? '#ff6b35' : '#f0f0f0',
              color: selectedCategory === 'all' ? '#fff' : '#555',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}
          >
            All
          </button>
          <button
            onClick={() => setSelectedCategory('__deals__')}
            style={{
              flexShrink: 0,
              padding: '12px 24px',
              borderRadius: 50,
              border: 'none',
              fontSize: 18,
              fontWeight: 700,
              cursor: 'pointer',
              background: selectedCategory === '__deals__' ? '#dc3545' : '#f0f0f0',
              color: selectedCategory === '__deals__' ? '#fff' : '#dc3545',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              whiteSpace: 'nowrap',
            }}
          >
            <i className="fas fa-tags" style={{ fontSize: 22 }}></i>
            Deals
          </button>
          {categories.map((cat) => {
            const catName = cat.name || cat.id;
            const isSelected = selectedCategory === catName;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(catName)}
                style={{
                  flexShrink: 0,
                  padding: '12px 24px',
                  borderRadius: 50,
                  border: 'none',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: isSelected ? '#ff6b35' : '#f0f0f0',
                  color: isSelected ? '#fff' : '#555',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  whiteSpace: 'nowrap',
                }}
              >
                {cat.imageUrl && (
                  <img
                    src={cat.imageUrl}
                    alt=""
                    style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                  />
                )}
                {catName}
              </button>
            );
          })}
          {/* Extra right spacer so last button isn't flush against edge */}
          <div style={{ flexShrink: 0, width: 8 }} />
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ padding: '12px 24px', background: '#fff', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
        <div style={{ position: 'relative', maxWidth: 480, margin: '0 auto' }}>
          <i
            className="fas fa-search"
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888',
              fontSize: 18,
              pointerEvents: 'none',
            }}
          ></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            style={{
              width: '100%',
              padding: '12px 44px 12px 48px',
              borderRadius: 50,
              border: '1px solid #e0e0e0',
              fontSize: 18,
              fontWeight: 600,
              background: '#f5f5f5',
              outline: 'none',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: 'none',
                background: '#ccc',
                color: '#fff',
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 24,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 24,
          alignContent: 'start',
        }}
      >
        {filteredProducts.map((product) => (
          <button
            key={product.id}
            onClick={() => addToCart(product)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#fff',
              borderRadius: 20,
              border: 'none',
              padding: 20,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              transition: 'transform 0.15s',
              textAlign: 'center',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                style={{
                  width: 140,
                  height: 140,
                  objectFit: 'cover',
                  borderRadius: 16,
                  marginBottom: 16,
                }}
              />
            ) : (
              <div
                style={{
                  width: 140,
                  height: 140,
                  borderRadius: 16,
                  background: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}
              >
                <i className="fas fa-utensils" style={{ fontSize: 40, color: '#ccc' }}></i>
              </div>
            )}
            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: 8,
                lineHeight: 1.2,
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {product.name}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#ff6b35' }}>
              {product.onSale && product.salePrice !== undefined ? (
                <>
                  <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '0.85rem', marginRight: 6 }}>
                    {formatCurrency(product.price)}
                  </span>
                  {formatCurrency(product.salePrice)}
                </>
              ) : (
                formatCurrency(product.price)
              )}
            </div>
            {product.onSale && (
              <span style={{
                background: '#dc3545',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 8,
                marginTop: 4,
              }}>
                SALE
              </span>
            )}
          </button>
        ))}
        {filteredProducts.length === 0 && (
          <div
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '80px 20px',
              color: '#888',
              fontSize: 20,
            }}
          >
            <i className="fas fa-search" style={{ fontSize: 48, marginBottom: 16, display: 'block' }}></i>
            No products found in this category
          </div>
        )}
      </div>

      {/* Cart Overlay */}
      {showCart && (
        <div
          onClick={() => setShowCart(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
          }}
        />
      )}

      {/* Cart Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 420,
          maxWidth: '90vw',
          height: '100vh',
          background: '#fff',
          boxShadow: showCart ? '-4px 0 24px rgba(0,0,0,0.15)' : 'none',
          transform: showCart ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Cart Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 800, color: '#1a1a1a' }}>
            <i className="fas fa-shopping-bag" style={{ marginRight: 12, color: '#ff6b35' }}></i>
            Your Order ({cartCount})
          </div>
          <button
            onClick={() => setShowCart(false)}
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              border: '1px solid #e0e0e0',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 20,
              color: '#666',
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Cart Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
              <i className="fas fa-shopping-basket" style={{ fontSize: 56, marginBottom: 16, display: 'block', color: '#ddd' }}></i>
              <div style={{ fontSize: 20, fontWeight: 700 }}>Your cart is empty</div>
              <div style={{ fontSize: 16, marginTop: 8 }}>Tap a product to add it</div>
            </div>
          ) : (
            cart.map((item, idx) => {
              const optionsKey = JSON.stringify(item.options || []);
              return (
                <div
                  key={`${item.productId}-${optionsKey}-${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '14px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {item.productImage ? (
                    <img
                      src={item.productImage}
                      alt=""
                      style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 12,
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i className="fas fa-utensils" style={{ color: '#ccc' }}></i>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>
                      {item.productName}
                    </div>
                    {item.options?.length > 0 && (
                      <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>
                        + {item.options.map((o) => (typeof o === 'string' ? o : o.itemName || o.name)).join(', ')}
                      </div>
                    )}
                    {item.instructions && (
                      <div style={{ fontSize: 12, color: '#ff6b35', marginBottom: 4, fontStyle: 'italic' }}>
                        <i className="fas fa-comment-alt mr-1" style={{ fontSize: 10 }}></i>
                        {item.instructions}
                      </div>
                    )}
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#ff6b35' }}>
                      {formatCurrency(item.total)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <button
                      onClick={() => updateQuantity(item.productId, -1, optionsKey)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: '1px solid #e0e0e0',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 14,
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <span style={{ fontSize: 18, fontWeight: 800, minWidth: 24, textAlign: 'center' }}>
                      {item.qty}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.productId, 1, optionsKey)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: '1px solid #e0e0e0',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 14,
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                    <button
                      onClick={() => removeFromCart(item.productId, optionsKey)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 16,
                        color: '#dc3545',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Cart Footer */}
        <div
          style={{
            padding: '20px 24px',
            borderTop: '1px solid #e0e0e0',
            background: '#fafafa',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 18, color: '#555' }}>
            <span>Subtotal ({cartCount} items)</span>
            <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(cartSubtotal)}</span>
          </div>
          {taxRate > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 18, color: '#555' }}>
              <span>Tax ({taxRate}%)</span>
              <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(cartTax)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, fontSize: 26, fontWeight: 800, color: '#1a1a1a' }}>
            <span>Total</span>
            <span style={{ color: '#ff6b35' }}>{formatCurrency(cartTotal)}</span>
          </div>
          <button
            onClick={handleGoToCheckout}
            disabled={cart.length === 0}
            style={{
              width: '100%',
              padding: '18px 0',
              borderRadius: 16,
              border: 'none',
              background: cart.length === 0 ? '#ccc' : '#ff6b35',
              color: '#fff',
              fontSize: 22,
              fontWeight: 800,
              cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
              boxShadow: cart.length === 0 ? 'none' : '0 6px 20px rgba(255,107,53,0.3)',
            }}
          >
            <span><i className="fas fa-credit-card" style={{ marginRight: 10 }}></i>Review & Pay — {formatCurrency(cartTotal)}</span>
          </button>
        </div>
      </div>

      {/* Back / Employee Verification Modal */}
      {showBackModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              width: '100%',
              maxWidth: 480,
              padding: '32px 28px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 800, color: '#1a1a1a', marginBottom: 8, textAlign: 'center' }}>
              <i className="fas fa-user-shield" style={{ color: '#ff6b35', marginRight: 10 }}></i>
              Staff Verification Required
            </div>
            <div style={{ fontSize: 16, color: '#888', marginBottom: 24, textAlign: 'center' }}>
              A kiosk session is active. Please verify your identity to access the admin panel.
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 14, fontWeight: 700, color: '#555', marginBottom: 6, display: 'block' }}>Employee</label>
              <select
                value={backEmployeeId}
                onChange={(e) => { setBackEmployeeId(e.target.value); setBackEmployeePin(''); }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: '1px solid #e0e0e0',
                  fontSize: 16,
                  background: '#fff',
                }}
              >
                <option value="">Select employee...</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} ({ROLES.find(r => r.key === emp.role)?.label || emp.role})
                  </option>
                ))}
              </select>
            </div>

            {backEmployeeId && employees.find((e) => e.id === backEmployeeId)?.pinHash && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 14, fontWeight: 700, color: '#555', marginBottom: 6, display: 'block' }}>Passkey</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={backEmployeePin}
                  onChange={(e) => setBackEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 4-6 digit passkey"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: 12,
                    border: '1px solid #e0e0e0',
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: 4,
                    textAlign: 'center',
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowBackModal(false)}
                style={{
                  flex: 1,
                  padding: '14px 0',
                  borderRadius: 12,
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  color: '#555',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={verifyBackEmployee}
                style={{
                  flex: 1,
                  padding: '14px 0',
                  borderRadius: 12,
                  border: 'none',
                  background: '#ff6b35',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Verify & Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Options Modal */}
      {showOptionsModal && optionsProduct && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              width: '100%',
              maxWidth: 640,
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {optionsProduct.imageUrl ? (
                  <img
                    src={optionsProduct.imageUrl}
                    alt={optionsProduct.name}
                    style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ width: 64, height: 64, borderRadius: 12, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-utensils" style={{ fontSize: 24, color: '#ccc' }}></i>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a' }}>
                    {optionsProduct.name}
                  </div>
                  {optionsProduct.description && (
                    <div style={{ fontSize: 15, color: '#888', marginTop: 4 }}>{optionsProduct.description}</div>
                  )}
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#ff6b35', marginTop: 4 }}>
                    {optionsProduct.onSale && optionsProduct.salePrice !== undefined ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '0.85rem', marginRight: 6 }}>
                          {formatCurrency(optionsProduct.price)}
                        </span>
                        {formatCurrency(optionsProduct.salePrice)}
                      </>
                    ) : (
                      formatCurrency(optionsProduct.price)
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setShowOptionsModal(false); setOptionsProduct(null); }}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 20,
                  color: '#666',
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {productOptions
                .filter((opt) => optionsProduct.optionIds?.includes(opt.id))
                .map((option) => (
                  <div key={option.id} style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 12 }}>
                      {option.name}
                      {option.optionRequired && <span style={{ color: '#dc3545' }}> *</span>}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {option.options?.map((optItem) => {
                        const isSelected = selectedOptions[option.id]?.has(optItem.name);
                        return (
                          <button
                            key={optItem.name}
                            type="button"
                            onClick={() => {
                              const newSelected = { ...selectedOptions };
                              const newFees = { ...optionsExtraFees };

                              if (option.allowMultipleSelection) {
                                newSelected[option.id] = new Set(newSelected[option.id] || []);
                                if (newSelected[option.id].has(optItem.name)) {
                                  newSelected[option.id].delete(optItem.name);
                                  delete newFees[optItem.name];
                                } else {
                                  newSelected[option.id].add(optItem.name);
                                  newFees[optItem.name] = optItem.extraFee || 0;
                                }
                              } else {
                                newSelected[option.id] = new Set([optItem.name]);
                                option.options?.forEach((oi) => delete newFees[oi.name]);
                                newFees[optItem.name] = optItem.extraFee || 0;
                              }

                              setSelectedOptions(newSelected);
                              setOptionsExtraFees(newFees);
                              setModalError('');
                            }}
                            style={{
                              padding: '12px 20px',
                              borderRadius: 50,
                              border: isSelected ? '2px solid #ff6b35' : '2px solid #e0e0e0',
                              background: isSelected ? '#fff3ee' : '#fff',
                              color: isSelected ? '#ff6b35' : '#555',
                              fontSize: 16,
                              fontWeight: 700,
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                          >
                            {optItem.name}
                            {optItem.extraFee > 0 ? ` +${formatCurrency(optItem.extraFee)}` : ''}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

              {/* Special Instructions */}
              {optionsProduct.acceptSpecialRequirement && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 12 }}>
                    Special Instructions
                  </div>
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Add any special requirements..."
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: 12,
                      border: '1px solid #e0e0e0',
                      fontSize: 16,
                      fontFamily: 'inherit',
                      minHeight: 80,
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}

              {/* Inline validation error */}
              {modalError && (
                <div style={{ color: '#dc3545', fontSize: 16, fontWeight: 700, marginBottom: 12, textAlign: 'center', padding: '12px 16px', background: '#f8d7da', borderRadius: 12 }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>
                  {modalError}
                </div>
              )}

              {/* Quantity Stepper */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 8, marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={() => setOptionsQty((q) => Math.max(1, q - 1))}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    border: '2px solid #e0e0e0',
                    background: '#fff',
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#555',
                    cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-minus"></i>
                </button>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a', minWidth: 40, textAlign: 'center' }}>
                  {optionsQty}
                </div>
                <button
                  type="button"
                  onClick={() => setOptionsQty((q) => Math.min(99, q + 1))}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    border: '2px solid #ff6b35',
                    background: '#fff',
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#ff6b35',
                    cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>

            <div
              style={{
                padding: '16px 24px',
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                gap: 12,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => { setShowOptionsModal(false); setOptionsProduct(null); }}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  borderRadius: 16,
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  color: '#555',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const applicableOptions = productOptions.filter((opt) =>
                    optionsProduct.optionIds?.includes(opt.id)
                  );
                  for (const option of applicableOptions) {
                    if (
                      option.optionRequired &&
                      (!selectedOptions[option.id] || selectedOptions[option.id].size === 0)
                    ) {
                      setModalError(`Please select required option: ${option.name}`);
                      return;
                    }
                  }
                  const extraFeeTotal = Object.values(optionsExtraFees).reduce((sum, fee) => sum + fee, 0);
                  const flatOptions = [];
                  for (const [optId, namesSet] of Object.entries(selectedOptions)) {
                    const optionDef = productOptions.find((o) => o.id === optId);
                    namesSet.forEach((name) => {
                      const optItem = optionDef?.options?.find((oi) => oi.name === name);
                      if (optItem) {
                        flatOptions.push({
                          optionId: optId,
                          optionName: optionDef?.name,
                          itemName: name,
                          extraFee: optItem.extraFee || 0,
                        });
                      }
                    });
                  }
                  finalizeAddToCart(optionsProduct, flatOptions, extraFeeTotal, specialInstructions, optionsQty);
                  setShowOptionsModal(false);
                  setOptionsProduct(null);
                  setSelectedOptions({});
                  setOptionsExtraFees({});
                  setSpecialInstructions('');
                  setModalError('');
                  setOptionsQty(1);
                }}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  borderRadius: 16,
                  border: 'none',
                  background: '#ff6b35',
                  color: '#fff',
                  fontSize: 18,
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(255,107,53,0.3)',
                }}
              >
                <i className="fas fa-plus" style={{ marginRight: 8 }}></i>
                Add {optionsQty > 1 ? `${optionsQty} to` : 'to'} Cart
                <span style={{ marginLeft: 8 }}>
                  {formatCurrency(
                    ((optionsProduct.onSale && optionsProduct.salePrice !== undefined ? optionsProduct.salePrice : (optionsProduct.price || 0))
                    + Object.values(optionsExtraFees).reduce((sum, fee) => sum + fee, 0)) * optionsQty
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Picker Modal (for simple products without options) */}
      {showQtyModal && qtyProduct && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              width: '100%',
              maxWidth: 480,
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {qtyProduct.imageUrl ? (
                  <img
                    src={qtyProduct.imageUrl}
                    alt={qtyProduct.name}
                    style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ width: 64, height: 64, borderRadius: 12, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-utensils" style={{ fontSize: 24, color: '#ccc' }}></i>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a' }}>
                    {qtyProduct.name}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#ff6b35', marginTop: 4 }}>
                    {qtyProduct.onSale && qtyProduct.salePrice !== undefined ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '0.85rem', marginRight: 6 }}>
                          {formatCurrency(qtyProduct.price)}
                        </span>
                        {formatCurrency(qtyProduct.salePrice)}
                      </>
                    ) : (
                      formatCurrency(qtyProduct.price)
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setShowQtyModal(false); setQtyProduct(null); }}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 20,
                  color: '#666',
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#555' }}>How many would you like?</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
                <button
                  type="button"
                  onClick={() => setQtyValue((q) => Math.max(1, q - 1))}
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    border: '2px solid #e0e0e0',
                    background: '#fff',
                    fontSize: 24,
                    fontWeight: 700,
                    color: '#555',
                    cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-minus"></i>
                </button>
                <div style={{ fontSize: 36, fontWeight: 800, color: '#1a1a1a', minWidth: 60, textAlign: 'center' }}>
                  {qtyValue}
                </div>
                <button
                  type="button"
                  onClick={() => setQtyValue((q) => Math.min(99, q + 1))}
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    border: '2px solid #ff6b35',
                    background: '#fff',
                    fontSize: 24,
                    fontWeight: 700,
                    color: '#ff6b35',
                    cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#ff6b35' }}>
                Total: {formatCurrency(
                  (qtyProduct.onSale && qtyProduct.salePrice !== undefined ? qtyProduct.salePrice : (qtyProduct.price || 0)) * qtyValue
                )}
              </div>
            </div>

            <div
              style={{
                padding: '16px 24px',
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                gap: 12,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => { setShowQtyModal(false); setQtyProduct(null); }}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  borderRadius: 16,
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  color: '#555',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  finalizeAddToCart(qtyProduct, [], 0, null, qtyValue);
                  setShowQtyModal(false);
                  setQtyProduct(null);
                  setQtyValue(1);
                }}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  borderRadius: 16,
                  border: 'none',
                  background: '#ff6b35',
                  color: '#fff',
                  fontSize: 18,
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(255,107,53,0.3)',
                }}
              >
                <i className="fas fa-plus" style={{ marginRight: 8 }}></i>
                Add {qtyValue > 1 ? `${qtyValue} to` : 'to'} Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KioskPage;
